import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Card, Spin, notification } from "antd";
import dayjs from "dayjs";

import DefaultLayout from "../../components/layout/DefaultLayout";
import RenderCustomFields from "../../components/renderCustomFields";

import dynamicFormService from "../../services/dynamicForm";
import customFieldService from "../../services/customField";
import commonCrud from "../../services/commonCrud";

const DynamicFormPage = () => {
  const { slug, id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [customfields, setCustomFields] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [recordData, setRecordData] = useState(null);
  const [recordId, setRecordId] = useState(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);


  // Fetch form + custom fields
  useEffect(() => {
    const fetchForm = async () => {
      try {
        setFetching(true);

        const res = await dynamicFormService.getFormBySlug(slug);
        const cf = await customFieldService.getcustomFields(res.id);

        const sorted = cf.sort((a, b) => a.sort - b.sort);
        setCustomFields(sorted);

        // Preload dynamic dropdowns
        await Promise.all(
          sorted.map(async (f) => {
            if (
              (f.data_type === "dynamic" || f.data_type === "lookups") &&
              f.data_reference
            ) {
              await fetchDynamicOptions(
                f.data_reference,
                f.data_type,
                f.sort_options
              );
            }
          })
        );
      } catch (err) {
        notification.error({
          message: "Failed to load form",
          description: err?.message || err,
        });
      } finally {
        setFetching(false);
      }
    };

    fetchForm();
  }, [slug]);

  // Fetch dynamic options
  const fetchDynamicOptions = async (
    reference,
    dataType = "dynamic",
    sortOption = "none"
  ) => {
    if (!reference || dynamicOptions[reference]) return;

    try {
      let options = [];

      if (dataType === "lookups") {
        const response = await commonCrud.lookups(reference);
        options = response.map((val) => ({
          label: val,
          value: val,
        }));
      } else {
        const apiMap = {
          departments: commonCrud.departments,
          cost_centers: commonCrud.cost_centers,
          users: () => commonCrud.users("all"),
          projects: commonCrud.projects,
          sections: commonCrud.sections,
          categories: commonCrud.categories,
          designations: commonCrud.designations,
          locations: commonCrud.locations,
          equipments: commonCrud.equipments,
          forms: commonCrud.forms,
          subscribers: commonCrud.subscribers,
          approvers: () => commonCrud.approvers(1, 100),
          makes: () => commonCrud.makes(1, 100),
          companies: () => commonCrud.companies(1, 100),
          services: () => commonCrud.services(1, 100),
        };

        const fetchFn = apiMap[reference];
        if (!fetchFn) return;

        const response = await fetchFn();

        if (Array.isArray(response)) {
          options = response.map((item) => ({
            label: item.name || item.title || item.id,
            value: item.id,
          }));
        }
      }

      if (sortOption === "asc")
        options.sort((a, b) => a.label.localeCompare(b.label));
      if (sortOption === "desc")
        options.sort((a, b) => b.label.localeCompare(a.label));

      setDynamicOptions((prev) => ({ ...prev, [reference]: options }));
    } catch (err) {
      console.error("Failed fetching options:", reference, err);
    }
  };

  // Fetch record for EDIT mode
  useEffect(() => {
    console.log("recordId changed:", recordId);
    if (!recordId) return;

    const fetchRecord = async () => {
      try {
        const record = await dynamicFormService.getRecordById(slug, recordId);
        console.log("Fetched record for edit:", record);
        setRecordData(record);
      } catch (err) {
        notification.error({
          message: "Record fetch failed",
          description: err?.message || err,
        });
      }
    };

    fetchRecord();
  }, [recordId, slug]);

  // Set form values after record + custom fields
  useEffect(() => {
    console.log("recordData", recordData);
    if (!recordData || !customfields.length) return;

    const values = {};

    // FIX: core fields come from recordData.core
    if (recordData.core && typeof recordData.core === "object") {
      Object.keys(recordData.core).forEach((key) => {
        values[key] = recordData.core[key];
      });
    }


    // FIX: custom_fields is an object { id: value }
    if (recordData.custom_fields && typeof recordData.custom_fields === "object") {
      const customFieldValues = {};

      Object.keys(recordData.custom_fields).forEach((id) => {
        const fieldKey = `field_${id}`;
        let value = recordData.custom_fields[id];

        const fieldMeta = customfields.find((f) => f.id == id);
        const type = fieldMeta?.data_type?.toLowerCase() || "";
        const label = fieldMeta?.label?.toLowerCase() || "";

        // date fields
        if (
          type.includes("date") ||
          label.includes("date") ||
          label.includes("dob") ||
          label.includes("day")
        ) {
          value = value ? dayjs(value) : null;
        }

        // multi select (comma separated)
        if (type === "multi") {
          value = value ? value.split(",").map((v) => v.trim()) : [];
        }

        customFieldValues[fieldKey] = value;
      });

      form.setFieldsValue(customFieldValues);
    }


    form.setFieldsValue(values);
  }, [recordData, customfields, dynamicOptions, form]);

  // Submit form
  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const coreFields = {};
      const customFields = {};

      Object.keys(values).forEach((key) => {
        if (key.startsWith("field_")) {
          const id = key.replace("field_", "");
          customFields[id] = Array.isArray(values[key])
            ? values[key].join(",")
            : values[key];
        } else {
          coreFields[key] = values[key];
        }
      });

      const payload = {
        ...coreFields,
        custom_fields: customFields,
      };

      if (recordId) payload.id = recordId;

      await dynamicFormService.submitFormBySlug(slug, payload);

      notification.success({ message: "Record saved successfully" });
      navigate(-1);
    } catch (err) {
      notification.error({
        message: "Save failed",
        description: err?.message || err,
      });
    } finally {
      setLoading(false);
    }
  };

  // Loader
  if (fetching)
    return (
      <DefaultLayout>
        <Card>
          <Spin tip="Loading form..." />
        </Card>
      </DefaultLayout>
    );

  return (
    <DefaultLayout>
      <Card title={`Dynamic Form - ${slug}`}>
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item
            name="request_title"
            label="Request Title"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <RenderCustomFields
            customfields={customfields}
            dynamicOptions={dynamicOptions}
            fetchDynamicOptions={fetchDynamicOptions}
          />

          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Card>
    </DefaultLayout>
  );
};

export default DynamicFormPage;
